import { buildTypeTree, placementOf, type TypeTree, type Placement } from './typeTree'

/**
 * Socket（端口）类型 —— 对齐契约 §2「值模型」
 *
 * 这里的类型就是连线时做类型校验、以及给 socket 上色的依据。
 * 注意：SocketType 与 WidgetKind 是两套正交的概念：
 *   - SocketType 描述「连线上流的是什么值」
 *   - WidgetKind 描述「节点上用什么控件编辑字面量」
 * 一个 number socket 旁边可以放 number widget，也可以是 enum widget（值域受限）。
 */
export type SocketType =
  /** 标量：文本 */
  | 'text'
  /** 标量：数值（浮点） */
  | 'number'
  /** 标量：布尔 */
  | 'boolean'
  /** 标量：颜色 RGBA(0..1) */
  | 'color'
  /** 标量：时间（epoch 毫秒） */
  | 'datetime'
  /** 富文本段数组（结构化，非内联标签） */
  | 'rich'
  /** 同类型元素集合 list<T> */
  | 'list'
  /** 引用值：页面 */
  | 'page'
  /** 引用值：布局实例（组件） */
  | 'component'
  /** 任意（用于 reroute / group 端口） */
  | 'any'

export const SOCKET_TYPES: readonly SocketType[] = [
  'text',
  'number',
  'boolean',
  'color',
  'datetime',
  'rich',
  'list',
  'page',
  'component',
  'any',
] as const

/**
 * v5 契约 §2 定义的隐式转换表。
 *
 * ⚠️ 当前连线规则**不使用**它 —— 连线按基础类型严格匹配（见 canConnect）。
 * 保留这张表是为了对齐契约文档，以及将来需要「宽松模式」时直接取用。
 * 类型转换请用函数节点显式表达（str / num / bool / color）。
 */
export const IMPLICIT_CONVERSIONS: Readonly<
  Partial<Record<SocketType, readonly SocketType[]>>
> = {
  number: ['text'],
  boolean: ['number', 'text'],
  color: ['number', 'text'],
  datetime: ['text'],
  rich: ['text'],
  text: [],
  list: [],
  page: [],
  component: [],
  any: [],
}

/** 既能传裸类型（`'text'`），也能传带元素类型的端口定义（`itemType` 是 `list` 专用） */
export type SocketSpec =
  | SocketType
  | { type: SocketType; itemType?: SocketType; strict?: boolean }

interface NormalizedSocket {
  type: SocketType
  itemType?: SocketType
  /** 精确连接（见 `SocketDef.strict`） */
  strict?: boolean
}

function normalizeSocket(spec: SocketSpec): NormalizedSocket {
  return typeof spec === 'string' ? { type: spec } : spec
}

/**
 * 列表元素类型的自动推导。
 *
 * 一侧声明了 `itemType`、另一侧没声明 → 用声明的一侧补齐。
 * 两侧都声明且不一致 → 推导失败，返回 undefined。
 */
export function resolveItemType(
  a?: SocketType,
  b?: SocketType,
): SocketType | undefined {
  if (a && b) return a === b ? a : undefined
  return a ?? b
}

/* ══════════════════════════════════════════════════════════════
   类型树
   ══════════════════════════════════════════════════════════════

   「标量」= 单一值，分两类：

     单一数据类型    text / number / boolean / color / datetime
                     —— 编辑器全知道（对应哪种控件、值域在哪）
     结构化数据类型   struct 子树（layout → component / page，还有 rich / list）
                      —— 编辑器只知道是个不透明结构，内部 schema 由节点处理方法定义

   连接看**最近共同祖先**，传值时向上**裁剪**到那个祖先。
   机制在 types/typeTree.ts，这里只是这份默认数据。
*/
export const DEFAULT_TYPE_TREE: TypeTree = buildTypeTree(
  [
    // ── 单一数据类型 ──
    { id: 'scalar' },
    { id: 'text', extends: 'scalar' },
    { id: 'number', extends: 'scalar' },
    { id: 'boolean', extends: 'scalar' },
    { id: 'color', extends: 'scalar' },
    { id: 'datetime', extends: 'scalar' },

    // ── 结构化数据类型 ──
    { id: 'struct', extends: 'scalar' },
    // layout 是「有位置 / 尺寸的东西」—— component 和 page 共享它，
    // 所以它们之间能互传布局数据（裁掉各自独有的部分）
    { id: 'layout', extends: 'struct' },
    { id: 'component', extends: 'layout' },
    { id: 'page', extends: 'layout' },
    { id: 'rich', extends: 'struct' },
    { id: 'list', extends: 'struct' },
  ],
  { structuredRoot: 'struct', wildcard: 'any' },
)

/**
 * 这条连接会怎么处理（放行 / 裁剪成什么 / 为什么不行）。
 *
 * 编辑器用它给出「会裁剪到 layout」这类提示 —— 光靠布尔值说不清。
 *
 * `list<T>` 的元素类型也走同一套判定，所以 `list<component>` 能接到
 * `list<page>` 上（元素逐项裁剪）—— 只读列表的自然协变。
 */
export function connectPlacement(
  from: SocketSpec,
  to: SocketSpec,
  tree: TypeTree = DEFAULT_TYPE_TREE,
): Placement {
  const a = normalizeSocket(from)
  const b = normalizeSocket(to)

  const base = placementOf(tree, a.type, b.type)
  if (!base.ok) return base

  /*
   * ★ **精确连接**（`strict`）：任一侧标了它就要求**完全同一个类型** ——
   * 不许向上转型、也不许"共享祖先、传值时裁一刀"。
   *
   * ⚠️ `any` 仍然放行（显式逃生口，见 `SocketDef.strict` 的说明）；
   * 两边都是 `list` 时元素类型也要完全相同（不然 `list<component>` 接到
   * `list<page>` 上会被裁成 `list<layout>` —— 那正是精确连接要挡的事）。
   */
  if (a.strict || b.strict) {
    if (a.type === 'any' || b.type === 'any') return base
    if (a.type !== b.type) {
      return { ok: false, reason: 'strict' }
    }
    if (a.itemType && b.itemType && a.itemType !== b.itemType) {
      return { ok: false, reason: 'strict' }
    }
  }

  // list<T>：元素类型也要兼容。未声明 = 待推导，视为兼容。
  if (a.type === 'list' && b.type === 'list' && a.itemType && b.itemType) {
    return placementOf(tree, a.itemType, b.itemType)
  }

  return base
}

/**
 * 两个端口能否连线。
 *
 * 规则见 `connectPlacement`。要点：
 *   · 任一侧 `any`（通配）→ 放行
 *   · 相同类型 / 向上转型 → 放行
 *   · 结构化类型共享祖先 → 放行，**传值时裁到那个祖先**
 *   · 单一数据类型之间不共享结构化祖先 → 不放行
 *
 * 刻意不做隐式转换（`number` → `text`）。端口配色就是类型约束的视觉表达：
 * 如果黄色的 `color` 端口能连到灰色的 `number` 端口，配色就失去意义了，
 * 用户也无法从颜色判断「什么可以链接什么」。
 *
 * 需要转换时插入函数节点（str / num / bool / color）——
 * 这也更贴合 v5 契约「动态值一律由节点图表达，不存在隐式语法」的取向。
 */
export function canConnect(
  from: SocketSpec,
  to: SocketSpec,
  tree: TypeTree = DEFAULT_TYPE_TREE,
): boolean {
  return connectPlacement(from, to, tree).ok
}
